import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { useEffect, useRef } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { ThemeProvider } from "@/hooks/use-theme";
import { trackPageView } from "@/lib/posthog";

import Home from "@/pages/public/home";
import Pricing from "@/pages/public/pricing";
import Login from "@/pages/auth/login";
import Register from "@/pages/auth/register";
import RegisterGym from "@/pages/auth/register-gym";
import ForgotPassword from "@/pages/auth/forgot-password";
import ResetPassword from "@/pages/auth/reset-password";
import StudentDashboard from "@/pages/student/dashboard";
import BookSession from "@/pages/student/book";
import FindTrainer from "@/pages/student/find-trainer";
import Sessions from "@/pages/student/sessions";
import Packages from "@/pages/student/packages";
import Memberships from "@/pages/student/memberships";
import StudentProfile from "@/pages/student/profile";
import GymSchedule from "@/pages/student/schedule";
import MasterCalendar from "@/pages/student/master-calendar";

import TrainerDashboard from "@/pages/trainer/dashboard";
import TrainerSessions from "@/pages/trainer/sessions";
import TrainerAvailability from "@/pages/trainer/availability";
import TrainerPayouts from "@/pages/trainer/payouts";
import TrainerProfile from "@/pages/trainer/profile";
import TrainerClients from "@/pages/trainer/clients";
import TrainerPerformance from "@/pages/trainer/performance";

import AdminDashboard from "@/pages/admin/dashboard";
import AdminSettings from "@/pages/admin/settings";
import AdminTrainers from "@/pages/admin/trainers";
import AdminCalendar from "@/pages/admin/calendar";
import AdminAnalytics from "@/pages/admin/analytics";
import AdminStudents from "@/pages/admin/students";
import AdminPayroll from "@/pages/admin/payroll";
import AdminBilling from "@/pages/admin/billing";
import AdminBranding from "@/pages/admin/branding";
import AdminCancellation from "@/pages/admin/cancellation";
import AdminNoShows from "@/pages/admin/no-shows";
import AdminMemberships from "@/pages/admin/memberships";
import AdminPackages from "@/pages/admin/packages";
import AdminConversations from "@/pages/admin/conversations";
import AdminRevenueSplit from "@/pages/admin/revenue-split";
import AdminMessaging from "@/pages/admin/messaging";
import AdminAiAssistant from "@/pages/admin/ai-assistant";
import AdminEngagement from "@/pages/admin/engagement";
import AdminSessionNotes from "@/pages/admin/session-notes";
import AdminSessions from "@/pages/admin/sessions";
import AdminSecurity from "@/pages/admin/security";

import NotificationsPage from "@/pages/notifications";
import ChatPage from "@/pages/chat";

import SuperadminDashboard from "@/pages/superadmin/dashboard";
import SuperadminGyms from "@/pages/superadmin/gyms";
import SuperadminGymDetail from "@/pages/superadmin/gym-detail";
import SuperadminUsers from "@/pages/superadmin/users";
import SuperadminAnalytics from "@/pages/superadmin/analytics";

import ExploreGyms from "@/pages/explore/gyms";
import ExploreTrainers from "@/pages/explore/trainers";
import GymProfile from "@/pages/explore/gym-profile";
import TrainerPublicProfile from "@/pages/explore/trainer-profile";
import TrainerOnboard from "@/pages/trainer/onboard";
import VideoSession from "@/pages/video-session";
import NotFound from "@/pages/not-found";
import { ChatProvider } from "@/hooks/use-chat";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Redirect to="/login" />;
  if (roles && !roles.includes(user.role)) return <Redirect to="/" />;
  return <>{children}</>;
}

function PageViewTracker() {
  const [location] = useLocation();
  const prevLocation = useRef(location);
  useEffect(() => {
    if (location !== prevLocation.current) {
      trackPageView(location);
      prevLocation.current = location;
    }
  }, [location]);
  useEffect(() => {
    trackPageView(location);
  }, []);
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  const pathname = location.split("#")[0];
  const prevPathname = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPathname.current) {
      window.scrollTo(0, 0);
      prevPathname.current = pathname;
    }
  }, [pathname]);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/register-gym" component={RegisterGym} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/explore/gyms" component={ExploreGyms} />
      <Route path="/explore/trainers" component={ExploreTrainers} />
      <Route path="/gym/:slug" component={GymProfile} />

      <Route path="/dashboard">{() => <ProtectedRoute roles={["STUDENT"]}><StudentDashboard /></ProtectedRoute>}</Route>
      <Route path="/book"><BookSession /></Route>
      <Route path="/find-trainer">{() => <ProtectedRoute roles={["STUDENT"]}><FindTrainer /></ProtectedRoute>}</Route>
      <Route path="/sessions">{() => <ProtectedRoute roles={["STUDENT"]}><Sessions /></ProtectedRoute>}</Route>
      <Route path="/video/:sessionId">{() => <ProtectedRoute roles={["STUDENT", "TRAINER"]}><VideoSession /></ProtectedRoute>}</Route>
      <Route path="/packages">{() => <ProtectedRoute roles={["STUDENT"]}><Packages /></ProtectedRoute>}</Route>
      <Route path="/memberships">{() => <ProtectedRoute roles={["STUDENT"]}><Memberships /></ProtectedRoute>}</Route>
      <Route path="/profile">{() => <ProtectedRoute roles={["STUDENT"]}><StudentProfile /></ProtectedRoute>}</Route>
      <Route path="/schedule">{() => <ProtectedRoute roles={["STUDENT"]}><GymSchedule /></ProtectedRoute>}</Route>
      <Route path="/master-calendar">{() => <ProtectedRoute roles={["STUDENT"]}><MasterCalendar /></ProtectedRoute>}</Route>
      <Route path="/messages">{() => <ProtectedRoute roles={["STUDENT"]}><ChatPage /></ProtectedRoute>}</Route>
      <Route path="/notifications">{() => <ProtectedRoute roles={["STUDENT", "TRAINER"]}><NotificationsPage /></ProtectedRoute>}</Route>

      <Route path="/trainer-onboard/:token" component={TrainerOnboard} />

      <Route path="/trainer/dashboard">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerDashboard /></ProtectedRoute>}</Route>
      <Route path="/trainer/sessions">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerSessions /></ProtectedRoute>}</Route>
      <Route path="/trainer/availability">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerAvailability /></ProtectedRoute>}</Route>
      <Route path="/trainer/payouts">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerPayouts /></ProtectedRoute>}</Route>
      <Route path="/trainer/profile">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerProfile /></ProtectedRoute>}</Route>
      <Route path="/trainer/clients">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerClients /></ProtectedRoute>}</Route>
      <Route path="/trainer/performance">{() => <ProtectedRoute roles={["TRAINER"]}><TrainerPerformance /></ProtectedRoute>}</Route>
      <Route path="/trainer/messages">{() => <ProtectedRoute roles={["TRAINER"]}><ChatPage /></ProtectedRoute>}</Route>

      <Route path="/trainer/:slug" component={TrainerPublicProfile} />

      <Route path="/admin/dashboard">{() => <ProtectedRoute roles={["ADMIN"]}><AdminDashboard /></ProtectedRoute>}</Route>
      <Route path="/admin/calendar">{() => <ProtectedRoute roles={["ADMIN"]}><AdminCalendar /></ProtectedRoute>}</Route>
      <Route path="/admin/revenue-split">{() => <ProtectedRoute roles={["ADMIN"]}><AdminRevenueSplit /></ProtectedRoute>}</Route>
      <Route path="/admin/trainers">{() => <ProtectedRoute roles={["ADMIN"]}><AdminTrainers /></ProtectedRoute>}</Route>
      <Route path="/admin/students">{() => <ProtectedRoute roles={["ADMIN"]}><AdminStudents /></ProtectedRoute>}</Route>
      <Route path="/admin/payroll">{() => <ProtectedRoute roles={["ADMIN"]}><AdminPayroll /></ProtectedRoute>}</Route>
      <Route path="/admin/analytics">{() => <ProtectedRoute roles={["ADMIN"]}><AdminAnalytics /></ProtectedRoute>}</Route>
      <Route path="/admin/billing">{() => <ProtectedRoute roles={["ADMIN"]}><AdminBilling /></ProtectedRoute>}</Route>
      <Route path="/admin/branding">{() => <ProtectedRoute roles={["ADMIN"]}><AdminBranding /></ProtectedRoute>}</Route>
      <Route path="/admin/cancellation">{() => <ProtectedRoute roles={["ADMIN"]}><AdminCancellation /></ProtectedRoute>}</Route>
      <Route path="/admin/no-shows">{() => <ProtectedRoute roles={["ADMIN"]}><AdminNoShows /></ProtectedRoute>}</Route>
      <Route path="/admin/memberships">{() => <ProtectedRoute roles={["ADMIN"]}><AdminMemberships /></ProtectedRoute>}</Route>
      <Route path="/admin/packages">{() => <ProtectedRoute roles={["ADMIN"]}><AdminPackages /></ProtectedRoute>}</Route>
      <Route path="/admin/settings">{() => <ProtectedRoute roles={["ADMIN"]}><AdminSettings /></ProtectedRoute>}</Route>
      <Route path="/admin/broadcast-messages">{() => <ProtectedRoute roles={["ADMIN"]}><AdminConversations /></ProtectedRoute>}</Route>
      <Route path="/admin/conversations"><Redirect to="/admin/broadcast-messages" /></Route>
      <Route path="/admin/messaging">{() => <ProtectedRoute roles={["ADMIN"]}><AdminMessaging /></ProtectedRoute>}</Route>
      <Route path="/admin/ai-assistant">{() => <ProtectedRoute roles={["ADMIN"]}><AdminAiAssistant /></ProtectedRoute>}</Route>
      <Route path="/admin/engagement">{() => <ProtectedRoute roles={["ADMIN"]}><AdminEngagement /></ProtectedRoute>}</Route>
      <Route path="/admin/session-notes">{() => <ProtectedRoute roles={["ADMIN"]}><AdminSessionNotes /></ProtectedRoute>}</Route>
      <Route path="/admin/sessions">{() => <ProtectedRoute roles={["ADMIN"]}><AdminSessions /></ProtectedRoute>}</Route>
      <Route path="/admin/security">{() => <ProtectedRoute roles={["ADMIN"]}><AdminSecurity /></ProtectedRoute>}</Route>

      <Route path="/superadmin">{() => <ProtectedRoute roles={["SUPERADMIN"]}><SuperadminDashboard /></ProtectedRoute>}</Route>
      <Route path="/superadmin/gyms">{() => <ProtectedRoute roles={["SUPERADMIN"]}><SuperadminGyms /></ProtectedRoute>}</Route>
      <Route path="/superadmin/gym/:id">{() => <ProtectedRoute roles={["SUPERADMIN"]}><SuperadminGymDetail /></ProtectedRoute>}</Route>
      <Route path="/superadmin/users">{() => <ProtectedRoute roles={["SUPERADMIN"]}><SuperadminUsers /></ProtectedRoute>}</Route>
      <Route path="/superadmin/analytics">{() => <ProtectedRoute roles={["SUPERADMIN"]}><SuperadminAnalytics /></ProtectedRoute>}</Route>

      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <AuthProvider>
              <ChatProvider>
                <PageViewTracker />
                <ScrollToTop />
                <Router />
                <Toaster />
              </ChatProvider>
            </AuthProvider>
          </WouterRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
